import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import updateLocale from 'dayjs/plugin/updateLocale';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { parseSmartDateInput } from '../../../../Utils/parseSmartDateInput';

import 'dayjs/locale/en';
import 'dayjs/locale/ar';
import 'dayjs/locale/he';

/* ───────── plug-ins ───────── */
dayjs.extend(utc);             // <<< keeps every instance in pure UTC
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);

function CustomDatePicker({
    date,
    onDateChange,
    label,
    height = '48px',
    disabled = false,
    error = false,
    clearable = false,
    iconPosition = 'right',
    smartParseOnEnter = false,
    textFieldClassName,
    inputSx,
}) {
    /* ---------- helpers ---------- */
    const normaliseToUtcStart = (djs) =>
        djs ? djs.utc().startOf('day') : null;

    const emitChange = (djs) => {
        const norm = normaliseToUtcStart(djs);
        if (norm) onDateChange(norm.toDate());
        else onDateChange(null);
    };

    const handleChange = (val) => {
        const utcMidnight = toUtcMidnight(val);
        if (utcMidnight) onDateChange(utcMidnight);
        else onDateChange(null);
    };

    /* ---------- state ---------- */
    const [inputValue, setInputValue] = useState(
        date ? dayjs.utc(date).format('DD/MM/YYYY') : '',
    );

    const { i18n, t } = useTranslation();
    const direction = i18n.dir();

    /* ---------- locale sync ---------- */
    useEffect(() => {
        dayjs.locale(i18n.language);
    }, [i18n.language]);

    /* ---------- sync inputValue with date prop ---------- */
    useEffect(() => {
        setInputValue(date ? dayjs.utc(date).format('DD/MM/YYYY') : '');
    }, [date]);

    /* ---------- debounced free-text handler ---------- */
    const debouncedEmit = useCallback(
        debounce((raw) => {
            const parsed = dayjs(raw, 'DD/MM/YYYY', true);
            const utcMidnight = toUtcMidnight(parsed);
            onDateChange(utcMidnight);
        }, 300),
        [onDateChange],
    );

    const toUtcMidnight = (djs) => {
        if (!djs?.isValid()) return null;

        const year = djs.get('year');
        const month = djs.get('month');   // 0-based
        const day = djs.get('date');      // day of month

        const jsDateUtc = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));


        return jsDateUtc;
    };


    /* ---------- render ---------- */
    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={i18n.language}
        >
            <DatePicker
                label={label}
                disabled={disabled}
                value={date ? dayjs.utc(date) : null}   /* always UTC */
                format="DD/MM/YYYY"
                inputValue={inputValue}
                /* ① Picker selection */
                onChange={handleChange}
                /* ② Typing */
                onInputChange={(_, newInput) => {
                    setInputValue(newInput);
                    const clean = newInput.replace(/[^\d/]/g, '');
                    debouncedEmit(clean);
                }}
                slotProps={{
                    ...(clearable && {
                        actionBar: {
                            actions: ['clear'],
                        },
                    }),
                    textField: {
                        fullWidth: true,
                        error,
                        variant: 'standard',
                        className: textFieldClassName,
                        placeholder: t('SelectDate') || 'Select Date',
                        onKeyDown: (e) => {
                            if (!smartParseOnEnter || e.key !== 'Enter' || e.shiftKey) return;
                            e.preventDefault();
                            const parsed = parseSmartDateInput(e.target?.value);
                            if (!parsed) return;
                            const utcMidnight = new Date(Date.UTC(
                                parsed.getFullYear(),
                                parsed.getMonth(),
                                parsed.getDate(),
                                0, 0, 0, 0,
                            ));
                            setInputValue(dayjs.utc(utcMidnight).format('DD/MM/YYYY'));
                            onDateChange(utcMidnight);
                        },
                        onBlur: (e) => {
                            if (!smartParseOnEnter) return;
                            const parsed = parseSmartDateInput(e.target?.value);
                            if (!parsed) return;
                            const utcMidnight = new Date(Date.UTC(
                                parsed.getFullYear(),
                                parsed.getMonth(),
                                parsed.getDate(),
                                0, 0, 0, 0,
                            ));
                            setInputValue(dayjs.utc(utcMidnight).format('DD/MM/YYYY'));
                            onDateChange(utcMidnight);
                        },
                        InputProps: {
                            disableUnderline: true,
                            className: `custom-box-sizing-border-box ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'
                                }`,
                            sx: {
                                height,
                                padding: '0 9px',
                                borderRadius: 'var(--Spacing-md, 8px)',
                                background: 'var(--bg-input, #F5F5F6)',
                                border: '1px solid transparent',
                                ...(iconPosition === 'left' && {
                                    '& .MuiInputAdornment-positionEnd': {
                                        order: -1,
                                        marginLeft: '8px',
                                        marginRight: 0,
                                    },
                                }),
                                ...inputSx,
                            },
                        },
                        inputProps: {
                            style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                        },
                    },
                    popper: {
                        style: {
                            zIndex: 10001
                        },
                        placement: direction === 'rtl' ? 'bottom-end' : 'bottom-start',
                        disablePortal: false,
                    },
                }}
            />
        </LocalizationProvider>
    );
}

export default CustomDatePicker;
